/**
 * WorkerSchedule — month-grid calendar view of the worker's
 * schedule (P0.3).
 *
 * Replaces the legacy 4-column table that read meApi.schedule.list().
 * Now renders a 7-column / 4-6 row month grid backed by the new
 * /v1/me/schedule/month endpoint, expanded server-side per-day:
 *   - timetable colour-bar (from Timetable.color)
 *   - scheduled_in / scheduled_out (HH:MM)
 *   - rest-day flag (weekly rest from AttendanceRule.rest_days OR
 *     cycle 'off' day)
 *   - holiday name (from PublicHoliday)
 *   - schedule_type chip ('fixed' / 'cycle' / 'temporary') so
 *     workers can spot the special-case day at a glance
 *
 * Mobile-friendly: the grid collapses gracefully to <360px because
 * each cell only carries 'D | timetable name | HH:MM-HH:MM' inline.
 *
 * Month nav: ← / Today / → buttons in the header. Initial view is
 * the current month. Out-of-range months still render (server clamps
 * to ge/le constraints) so we don't have to bake range logic here.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, CircularProgress, Paper, IconButton, Button,
  Stack, Chip, Tooltip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as PresentIcon,
  Alarm as LateIcon,
  DirectionsWalk as EarlyIcon,
  PlayArrow as MissedInIcon,
  Stop as MissedOutIcon,
  Cancel as AbsentIcon,
  // Approved leave. An umbrella/parasol is the leave metaphor this app already uses for
  // leave types and leave policies, and it asserts no REASON for the absence — which a
  // plane or a bed would (svdy-ui §14).
  BeachAccess as LeaveIcon,
  // A briefcase, not a plane: the product does not know whether the trip is by air, rail
  // or road, and an icon is a claim (svdy-ui §14).
  BusinessCenter as TripIcon,
  LocalCafe as RestIcon,
  Star as HolidayIcon,
  RadioButtonUnchecked as ScheduledIcon,
  Remove as NoShiftIcon,
} from '@mui/icons-material';
import { meApi } from '../../api';
import type { MonthScheduleResponse, MonthDay, DayEntry } from '../../api/me';
import { useTranslation } from 'react-i18next';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function isoToday(): { year: number; month: number; day: number } {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function monthLabel(year: number, month: number, locale: string): string {
  // Locale-aware "May 2026" / "2026年5月" / "Mai 2026" via Intl.
  const ref = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long' }).format(ref);
}

/**
 * Lay the month into a 7-column grid keyed by ISO date. Pads the
 * first row with leading empties so the first day of the month
 * lands under its real weekday. Pads the trailing row likewise.
 * Returns rows of 7 cells; each cell is either a MonthDay or null.
 */
function gridify(days: MonthDay[]): (MonthDay | null)[][] {
  if (days.length === 0) return [];
  // Determine weekday of day 1 in the [Mon..Sun] order WEEKDAY_LABELS uses.
  const first = days[0];
  const firstWeekdayIdx = WEEKDAY_LABELS.indexOf(first.weekday);
  const padFront = firstWeekdayIdx >= 0 ? firstWeekdayIdx : 0;

  const cells: (MonthDay | null)[] = [];
  for (let i = 0; i < padFront; i++) cells.push(null);
  for (const d of days) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const rows: (MonthDay | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }
  return rows;
}

function isToday(date: string): boolean {
  const t = isoToday();
  return date === `${t.year}-${pad2(t.month)}-${pad2(t.day)}`;
}

/**
 * Attendance marker for a day — ONE glyph per day, on the same vocabulary the phone uses.
 *
 * Colours and icon SHAPES are kept in lockstep with `svdy-mobile/app/(tabs)/schedule.tsx`
 * (STATUS_COLOR / STATUS_ICON), so a worker who checks their month on the phone and then
 * on the web sees the same marker for the same day. Shape is the primary differentiator:
 * colour alone is ambiguous at a 15px glyph, and four statuses used to collapse into two
 * hues (mobile rule, owner review 2026-06-25).
 *
 * Families: green = good · amber→orange = timing · reds = missing/absent (bright → deep
 * magenta → maroon) · TEAL/BLUE = AUTHORISED (leave, business trip — a day the manager
 * approved is not an exception and must never be in the red family) · cool greys/purple =
 * rest, holiday, scheduled, no shift.
 */
const STATUS_COLORS = {
  late: '#F0A020',       // amber
  early: '#EC6608',      // burnt orange
  missedIn: '#E5484D',   // bright red — missing clock-IN
  missedOut: '#C2185B',  // deep magenta — a clearly different hue from the red
  absent: '#8B1A1A',     // dark maroon
  onTime: '#1FA855',     // green
  leave: '#0F9B8E',      // teal — AUTHORISED
  trip: '#2563EB',       // blue — AUTHORISED
  rest: '#6B7280',       // slate
  holiday: '#7B43C7',    // purple
  scheduled: '#3067D1',  // brand blue — planned, hasn't happened yet
  noShift: '#B0B5BC',    // light grey — no shift assigned
} as const;

export type MarkerKind =
  | 'leave' | 'trip' | 'absent' | 'missedIn' | 'missedOut'
  | 'late' | 'early' | 'onTime'
  | 'holiday' | 'rest' | 'scheduled' | 'noShift';

export interface DayMarker {
  kind: MarkerKind;
  color: string;
  /** Rendered under the plan. Null for the quiet states the cell body already spells out. */
  label: string | null;
  Icon: typeof LateIcon;
}

/** Everything the ladder below reads. Narrowed so the ladder is testable without a render. */
export type MarkerInput = Pick<
  DayEntry,
  'status' | 'missed_clock_in' | 'missed_clock_out'
>;

/**
 * The status ladder. FIRST MATCH WINS, and the ORDER IS THE CONTRACT.
 *
 * `AUTHORISED SITS ABOVE THE MISSED-PUNCH CHECKS.` The backend sets `missed_clock_in` on
 * ANY scheduled day with no punches — including a day covered by approved leave, where the
 * flag exists only to enable the missed-punch correction action and must NOT drive the
 * badge. Reported on the phone 2026-09-03 as 缺少上班打卡 on an approved leave day, whose
 * ladder had branches for absent and business trip but none for leave. `absent` is above
 * them for the same reason: an elapsed scheduled day with no punch at all is a no-show, not
 * a forgotten punch.
 *
 * `half_day` is deliberately absent. `/v1/me/time-entries/daily` never emits it — its
 * status derivation (`app/me/service.py`) writes only on_time / late / early_leave /
 * incomplete / absent / leave / business_trip / rest / holiday / scheduled / unscheduled /
 * no_data, and a genuine half-day leave keeps its punch-derived status because the worker
 * really did work the other half. `half_day` exists only in the DailySummary written by
 * `app/attendance/calculation.py`. A branch here would be unreachable and a legend row for
 * it would advertise a badge nobody can ever see.
 */
export function dayMarker(
  actual: MarkerInput | undefined,
  planned: { is_holiday?: boolean; is_rest_day?: boolean; is_cycle_off_day?: boolean; scheduled_in?: string | null },
  t: (k: string) => string,
): DayMarker | null {
  const status = actual?.status;

  // AUTHORISED — above every punch-completeness check. Do not reorder.
  if (status === 'leave') {
    return { kind: 'leave', color: STATUS_COLORS.leave, label: t('worker:schedule.statusLeave'), Icon: LeaveIcon };
  }
  if (status === 'business_trip') {
    return { kind: 'trip', color: STATUS_COLORS.trip, label: t('worker:schedule.statusBusinessTrip'), Icon: TripIcon };
  }
  // A no-show outranks "forgot a punch": the backend sets BOTH missed flags on such a day.
  if (status === 'absent') {
    return { kind: 'absent', color: STATUS_COLORS.absent, label: t('worker:schedule.statusAbsent'), Icon: AbsentIcon };
  }
  // A PUBLIC HOLIDAY AND A REST DAY ARE ALSO AUTHORISED, and belong above the punch checks.
  //
  // Reported 2026-09-08 on the phone, and this ladder had it too: a holiday rendered correctly until it
  // became a PAST day, then showed "missed clock-in". The backend sets `missed_clock_in` on any scheduled
  // day with no punches purely to enable the correction action — the note above already says AUTHORISED
  // sits above every punch-completeness check — and `leave`, `business_trip` and `absent` were each given
  // that place while `holiday` and `rest` were left at the bottom with the "nothing happened" fallbacks.
  //
  // Nobody is missing a punch, late, or leaving early on a day they were not scheduled to work. Kept as
  // one branch reading BOTH the actual status and the planned flag, because the planned side is the
  // fallback when the daily fetch failed — that behaviour is unchanged, only its position.
  if (status === 'holiday' || planned.is_holiday) {
    return { kind: 'holiday', color: STATUS_COLORS.holiday, label: null, Icon: HolidayIcon };
  }
  if (status === 'rest' || planned.is_rest_day || planned.is_cycle_off_day) {
    return { kind: 'rest', color: STATUS_COLORS.rest, label: null, Icon: RestIcon };
  }
  // Which punch is missing is the actionable half, so the two are separate markers rather
  // than one "Missed punch" — the worker knows which end to correct.
  if (actual?.missed_clock_in) {
    return { kind: 'missedIn', color: STATUS_COLORS.missedIn, label: t('worker:schedule.statusMissedIn'), Icon: MissedInIcon };
  }
  if (actual?.missed_clock_out) {
    return { kind: 'missedOut', color: STATUS_COLORS.missedOut, label: t('worker:schedule.statusMissedOut'), Icon: MissedOutIcon };
  }
  // Backstop for the contract the two branches above depend on: the server sets
  // status='incomplete' if and only if one of those flags is set. If that ever drifts, an
  // incomplete day would fall through to 'scheduled' below and read as if nothing were
  // wrong. Fall back to the in-punch, which is the side a worker is asked to correct.
  if (status === 'incomplete') {
    return { kind: 'missedIn', color: STATUS_COLORS.missedIn, label: t('worker:schedule.statusMissedIn'), Icon: MissedInIcon };
  }
  if (status === 'late') {
    return { kind: 'late', color: STATUS_COLORS.late, label: t('worker:schedule.statusLate'), Icon: LateIcon };
  }
  if (status === 'early_leave') {
    return { kind: 'early', color: STATUS_COLORS.early, label: t('worker:schedule.statusEarly'), Icon: EarlyIcon };
  }
  // Worked and nothing to flag. No label — a normal day should be quiet, so the coloured
  // text band below is reserved for the days that need reading. `no_data` is a punch
  // outside any schedule: the person WAS present, which is what the green check says.
  if (status === 'on_time' || status === 'no_data') {
    return { kind: 'onTime', color: STATUS_COLORS.onTime, label: null, Icon: PresentIcon };
  }

  // Nothing happened, and nothing was owed. These fall back to the PLANNED day when the
  // daily fetch failed (it is non-fatal — the grid still renders).
  //
  // `holiday` and `rest` used to be here and moved above the punch checks on 2026-09-08 — see the note
  // there. They are NOT duplicated here: two branches for one condition would make the first unreachable
  // and hide which one the day actually took.
  if (status === 'scheduled' || planned.scheduled_in) {
    return { kind: 'scheduled', color: STATUS_COLORS.scheduled, label: null, Icon: ScheduledIcon };
  }
  if (status === 'unscheduled' || status === undefined) {
    return { kind: 'noShift', color: STATUS_COLORS.noShift, label: null, Icon: NoShiftIcon };
  }
  return null;
}

/**
 * Legend rows, in ladder order. Exported so a test can assert the legend covers every
 * marker the ladder can produce — a marker with no legend row is an unexplained glyph, and
 * a legend row no ladder can produce advertises a badge nobody will ever see.
 */
export const LEGEND_ROWS: { kind: MarkerKind; color: string; key: string; Icon: typeof LateIcon }[] = [
  { kind: 'onTime', color: STATUS_COLORS.onTime, key: 'statusOnTime', Icon: PresentIcon },
  { kind: 'late', color: STATUS_COLORS.late, key: 'statusLate', Icon: LateIcon },
  { kind: 'early', color: STATUS_COLORS.early, key: 'statusEarly', Icon: EarlyIcon },
  { kind: 'missedIn', color: STATUS_COLORS.missedIn, key: 'statusMissedIn', Icon: MissedInIcon },
  { kind: 'missedOut', color: STATUS_COLORS.missedOut, key: 'statusMissedOut', Icon: MissedOutIcon },
  { kind: 'absent', color: STATUS_COLORS.absent, key: 'statusAbsent', Icon: AbsentIcon },
  { kind: 'leave', color: STATUS_COLORS.leave, key: 'statusLeave', Icon: LeaveIcon },
  { kind: 'trip', color: STATUS_COLORS.trip, key: 'statusBusinessTrip', Icon: TripIcon },
  { kind: 'rest', color: STATUS_COLORS.rest, key: 'legendRest', Icon: RestIcon },
  { kind: 'holiday', color: STATUS_COLORS.holiday, key: 'legendHoliday', Icon: HolidayIcon },
  { kind: 'scheduled', color: STATUS_COLORS.scheduled, key: 'statusScheduled', Icon: ScheduledIcon },
  { kind: 'noShift', color: STATUS_COLORS.noShift, key: 'statusNoShift', Icon: NoShiftIcon },
];

/**
 * kind → legend key, DERIVED from the rows above rather than written a second time. The
 * quiet markers (rest / holiday / scheduled / no shift) carry no label in the cell, so the
 * glyph's tooltip has to name them — and the name it gives must be the one the legend gives
 * (svdy-rules §10.5b).
 */
export const LEGEND_KEY_BY_KIND: Record<MarkerKind, string> = LEGEND_ROWS.reduce(
  (acc, row) => {
    acc[row.kind] = row.key;
    return acc;
  },
  {} as Record<MarkerKind, string>,
);

/**
 * One day cell, in THREE bands with distinct jobs. Reported by the owner 2026-09-03:
 * 每天的信息很杂乱 · 一眼望过去不知道标注的是哪天 · 上下风格不明显 · 每个横杠是啥.
 *
 *   band 1 — WHICH DAY: the date, the heaviest thing in the cell, plus the day's ONE
 *            status glyph on the right. The date used to be `caption`/500 at 0.8rem, no
 *            heavier than the timetable name, so no cell announced itself.
 *   band 2 — THE PLAN: the timetable colour as a VERTICAL rail attached to the shift name.
 *            It used to be a 3px FULL-WIDTH horizontal bar under the date, which reads as a
 *            divider and belongs to nothing — and the legend swatch for it was hardcoded
 *            green while the real bar takes `Timetable.color`, so the legend could not
 *            explain it. Grey text: this is what was planned.
 *   band 3 — WHAT HAPPENED: the status label, in the status colour, and ONLY for the days
 *            that need reading. Plan grey above, outcome coloured below — the two layers
 *            are now visually different, which they were not.
 */
const DayCell: React.FC<{ day: MonthDay | null; rowHeight: number; actual?: DayEntry }> = ({
  day, rowHeight, actual,
}) => {
  const { t } = useTranslation();
  if (!day) {
    return (
      <Box
        sx={{
          minHeight: rowHeight,
          borderRight: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      />
    );
  }
  const dayNum = parseInt(day.date.slice(8, 10), 10);
  const today = isToday(day.date);
  const restNoSchedule = day.is_rest_day && !day.timetable_name;
  const marker = dayMarker(actual, day, t);
  const rail = day.timetable_color || (day.is_holiday ? STATUS_COLORS.holiday : null);

  return (
    <Box
      sx={{
        minHeight: rowHeight,
        borderRight: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        px: 0.75,
        py: 0.5,
        bgcolor: today ? 'action.hover' : restNoSchedule ? 'grey.50' : 'background.paper',
        display: 'flex',
      }}
    >
      {/* ONE left axis for everything in the cell. Measured off the owner's screenshot at
          2x: the date box sat at 6px, the plan text at 16.5px (pushed by the rail plus its
          gap) and the status label back at 6px — three origins inside one 120px cell, which
          is svdy-ui §15 at cell scale. The rail now owns a reserved left COLUMN, so the date,
          the plan and the outcome all start at the same inset and the colour reads as the
          day's spine rather than as something wedged between two lines.

          The column is reserved even when there is no colour (rest days, unscheduled), or
          those cells would put their text 9px left of every other cell's. Same reasoning as
          the non-breaking-space placeholder in §11.3, applied horizontally. */}
      <Box sx={{ display: 'flex', gap: 0.75, flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: 3,
            flexShrink: 0,
            borderRadius: 1,
            bgcolor: rail ?? 'transparent',
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, minWidth: 0 }}>
          {/* WHICH DAY. */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Typography
                component="span"
                sx={{
                  // One declaration, because a second one after the spread below would be
                  // silently overwritten (TS2783). Today's number sits inside a disc, so it
                  // needs to be a shade smaller to stay centred in it.
                  fontSize: today ? '0.75rem' : '0.9375rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  // Today is a filled disc, so the current day is findable without reading
                  // any number — the same affordance the phone calendar uses.
                  ...(today
                    ? {
                        width: 19,
                        height: 19,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }
                    : {
                        color: day.is_holiday
                          ? 'secondary.main'
                          : day.is_rest_day
                            ? 'text.secondary'
                            : 'text.primary',
                      }),
                }}
              >
                {dayNum}
              </Typography>
              {day.schedule_type === 'temporary' && (
                <Tooltip title={t('worker:schedule.tempTooltip')}>
                  <Chip
                    label={t('worker:schedule.tmpAbbr')}
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{ height: 14, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
                  />
                </Tooltip>
              )}
            </Box>
            {marker && (
              <Tooltip title={marker.label ?? t(`worker:schedule.${LEGEND_KEY_BY_KIND[marker.kind]}`)}>
                <marker.Icon sx={{ fontSize: 14, color: marker.color, flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>

          {/* THE PLAN, in grey. */}
          {day.is_holiday && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: STATUS_COLORS.holiday, fontWeight: 600, fontSize: '0.65rem', lineHeight: 1.25 }}
              noWrap
              title={day.holiday_name ?? ''}
            >
              {day.holiday_name ?? t('worker:schedule.holiday')}
            </Typography>
          )}
          {day.timetable_name ? (
            <>
              <Typography
                variant="caption"
                sx={{ display: 'block', fontSize: '0.65rem', lineHeight: 1.25, color: 'text.secondary' }}
                noWrap
                title={day.timetable_name}
              >
                {day.timetable_name}
              </Typography>
              {day.scheduled_in && day.scheduled_out && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.25 }}
                >
                  {day.scheduled_in}–{day.scheduled_out}
                </Typography>
              )}
            </>
          ) : day.is_rest_day && !day.is_holiday ? (
            <Typography
              variant="caption"
              sx={{ display: 'block', fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.25 }}
            >
              {day.is_cycle_off_day ? t('worker:schedule.cycleOff') : t('worker:schedule.rest')}
            </Typography>
          ) : null}

          {/* WHAT HAPPENED. */}
          {marker?.label && (
            <Typography
              variant="caption"
              sx={{
                mt: 'auto',
                fontSize: '0.625rem',
                color: marker.color,
                fontWeight: 700,
                lineHeight: 1.2,
              }}
              noWrap
              title={marker.label}
            >
              {marker.label}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const WorkerSchedule: React.FC = () => {
  const { t, i18n } = useTranslation();
  const today0 = isoToday();
  const [year, setYear] = useState(today0.year);
  const [month, setMonth] = useState(today0.month);
  const [data, setData] = useState<MonthScheduleResponse | null>(null);
  const [daily, setDaily] = useState<Map<string, DayEntry>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMonth = useCallback(async (y: number, m: number) => {
    setLoading(true);
    setError(null);
    try {
      const lastDay = new Date(y, m, 0).getDate();
      const start = `${y}-${pad2(m)}-01`;
      const end = `${y}-${pad2(m)}-${pad2(lastDay)}`;
      const [body, dailyRes] = await Promise.all([
        meApi.schedule.month(y, m),
        // Actual attendance (late/early/missed/absent) for the month.
        // Non-fatal — the planned grid still renders if this fails.
        meApi.timeEntries.daily(start, end).catch(() => null),
      ]);
      setData(body);
      const map = new Map<string, DayEntry>();
      for (const d of dailyRes?.days ?? []) map.set(d.date, d);
      setDaily(map);
    } catch (e) {
      setError((e as Error).message ?? t('common:feedback.loadFailed'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchMonth(year, month);
  }, [year, month, fetchMonth]);

  const rows = useMemo(() => gridify(data?.days ?? []), [data]);
  // Sized to the content rather than guessed: today's disc 19 + gap 2 + shift name 13 +
  // gap 2 + time range 12 + gap 2 + outcome 12 = 62, plus 8 of vertical padding = 70. 76
  // leaves one line of slack without the dead band the owner objected to; six rows of it
  // save 120px against the 96 this replaced, which is the "更紧凑" ask.
  const rowHeight = 76;

  const goPrev = () => {
    if (month === 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else {
      setMonth((m) => m - 1);
    }
  };
  const goNext = () => {
    if (month === 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else {
      setMonth((m) => m + 1);
    }
  };
  const goToday = () => {
    const td = isoToday();
    setYear(td.year);
    setMonth(td.month);
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      py: '12px',
      width: '100%',
    }}>
      {/* Page header */}
      <Box sx={{ mb: 1.5, width: '100%' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('worker:schedule.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('worker:schedule.monthSubtitle')}
        </Typography>
      </Box>

      {/* Month navigation row */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ width: '100%', mb: 1.5 }}
      >
        <IconButton size="small" onClick={goPrev} disabled={loading}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Button
          size="small"
          onClick={goToday}
          disabled={loading}
          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.8rem' }}
        >
          {t('worker:schedule.today')}
        </Button>
        <IconButton size="small" onClick={goNext} disabled={loading}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, fontSize: '1rem', ml: 1 }}
        >
          {monthLabel(year, month, i18n.language)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {loading && <CircularProgress size={16} />}
      </Stack>

      {/* Error state */}
      {error && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: 2,
            mb: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'error.main',
          }}
        >
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Paper>
      )}

      {/* Calendar grid */}
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {/* Weekday header row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            bgcolor: 'grey.100',
          }}
        >
          {WEEKDAY_LABELS.map((wd, idx) => {
            // Reference Mon=Jan 1 2024, Tue=Jan 2 ... Sun=Jan 7. Locale-aware short weekday.
            const ref = new Date(2024, 0, idx + 1);
            const localized = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' }).format(ref);
            return (
            <Box
              key={wd}
              sx={{
                px: 1,
                py: 0.75,
                borderRight: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
                ':last-child': { borderRight: 'none' },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  letterSpacing: 0.5,
                }}
              >
                {localized}
              </Typography>
            </Box>
            );
          })}
        </Box>

        {/* Body rows */}
        {rows.length === 0 && !loading ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('worker:schedule.noData')}
            </Typography>
          </Box>
        ) : (
          rows.map((row, rIdx) => (
            <Box
              key={rIdx}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
              }}
            >
              {row.map((cell, cIdx) => (
                <DayCell
                  key={cIdx}
                  day={cell}
                  rowHeight={rowHeight}
                  actual={cell ? daily.get(cell.date) : undefined}
                />
              ))}
            </Box>
          ))
        )}
      </Paper>

      {/* Legend — colours and icon shapes match the phone, row for row (LEGEND_ROWS).
          The shift-colour swatch is a VERTICAL rail, like the one in the cells, and it is
          neutral grey on purpose: the real rail takes `Timetable.color`, so no single
          swatch can be its colour. The old legend claimed a fixed light green, which is why
          the bars in the grid were unexplainable. */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 1.5, flexWrap: 'wrap' }}
        useFlexGap
      >
        {LEGEND_ROWS.map((s) => (
          <Box key={s.kind} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <s.Icon sx={{ fontSize: 13, color: s.color }} />
            <Typography variant="caption" color="text.secondary">{t(`worker:schedule.${s.key}`)}</Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 3, height: 13, bgcolor: 'text.disabled', borderRadius: 1 }} />
          <Typography variant="caption" color="text.secondary">{t('worker:schedule.legendShift')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Chip label={t('worker:schedule.tmpAbbr')} size="small" variant="outlined" color="warning"
            sx={{ height: 14, fontSize: '0.6rem' }} />
          <Typography variant="caption" color="text.secondary">{t('worker:schedule.legendTemp')}</Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default WorkerSchedule;
